import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { CircleCheckBig, decorativeIcon, Info, Lock, TriangleAlert } from '../../lib/icons';
import type { StatusTone } from '../../lib/status';
import { StatusChip } from '../StatusChip/StatusChip';

/**
 * The full-bleed message band, and its contained sibling.
 *
 * DESIGN.md's refusal container rule: OUTSIDE a dialog a refusal is a
 * full-bleed band at radius 0 that pushes content down; INSIDE a dialog it is a
 * contained 4 px alert. Both carry a solid status field with near-black text,
 * so they render at the same ratio on both themes.
 *
 * **There is no coloured left-edge stripe here, and there is no prop that could
 * produce one.** Status lives in the fill and the word, never in the furniture.
 */
const banner = cva(['flex w-full flex-wrap items-start gap-3 text-start'], {
  variants: {
    tone: {
      success: 'bg-status-success text-status-success-fg',
      warning: 'bg-status-warning text-status-warning-fg',
      danger: 'bg-status-danger text-status-danger-fg',
      info: 'bg-status-info text-status-info-fg',
      neutral: 'bg-status-neutral text-status-neutral-fg',
      'neutral-strong': 'bg-status-neutral-strong text-status-neutral-strong-fg',
    },
    density: {
      /** The blocking band: full-bleed, radius 0, min 48 px. */
      band: 'min-h-band rounded-band px-4 py-3',
      /** Inside a dialog or a panel: contained, 4 px radius. */
      inset: 'rounded-control px-3 py-2.5',
    },
  },
  defaultVariants: { tone: 'danger', density: 'band' },
});

export type BannerVariants = VariantProps<typeof banner>;

const TONE_ICON = {
  success: CircleCheckBig,
  warning: TriangleAlert,
  danger: TriangleAlert,
  info: Info,
  neutral: Info,
  'neutral-strong': Lock,
} as const;

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, BannerVariants {
  readonly tone: StatusTone;
  /** The state, in a word or a short phrase. Already translated. */
  readonly title: ReactNode;
  /** A sentence naming what to do next. No error codes on a worker surface. */
  readonly description?: ReactNode;
  /** A link or button — the resolution. */
  readonly action?: ReactNode;
  /**
   * `status` for something the user did (polite), `alert` for something that
   * arrived and blocks them (assertive). A blocking-exception banner is an
   * alert; a "saved" banner is not.
   */
  readonly live?: 'status' | 'alert' | 'none';
  /** Replaces the tone's default glyph. */
  readonly icon?: ReactNode;
}

export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { tone, density, title, description, action, live = 'status', icon, className, ...rest },
  ref,
) {
  const ToneIcon = TONE_ICON[tone];

  return (
    <div
      {...rest}
      ref={ref}
      {...(live === 'none' ? {} : { role: live })}
      className={cn(banner({ tone, density }), className)}
    >
      <span aria-hidden="true" className="mt-px inline-flex shrink-0">
        {icon ?? <ToneIcon {...decorativeIcon('lg')} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-subject">{title}</p>
        {description !== undefined ? (
          <p className="mt-0.5 text-body-compact">{description}</p>
        ) : null}
      </div>
      {action !== undefined ? (
        <div className="min-w-0 max-w-full basis-full break-words sm:basis-auto">{action}</div>
      ) : null}
    </div>
  );
});

export interface GateNoticeProps {
  /** The status word — "Verrouillée", "Fermée", "Non disponible". Translated. */
  readonly state: ReactNode;
  /** Why it is closed, and WHEN it opens: "S'ouvre le 30 août à 00 h 01." */
  readonly reason: ReactNode;
  /** The disabled primary this notice explains. */
  readonly action?: ReactNode;
  readonly tone?: Extract<StatusTone, 'neutral' | 'neutral-strong' | 'warning'>;
  readonly className?: string;
}

/**
 * "This action is locked until X."
 *
 * A disabled button with no sentence beneath it is the most common way this
 * product could lie: the worker or supervisor cannot tell whether the app is
 * broken, whether they lack permission, or whether they are simply early. The
 * gate always names the state, the reason and the moment it opens.
 */
export function GateNotice({
  state,
  reason,
  action,
  tone = 'neutral-strong',
  className,
}: GateNoticeProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-panel border border-border bg-surface-sunk p-4',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-px inline-flex shrink-0 text-ink-muted">
          <Lock {...decorativeIcon('lg')} />
        </span>
        <div className="min-w-0">
          <StatusChip tone={tone}>{state}</StatusChip>
          <p className="mt-2 text-body-compact text-ink">{reason}</p>
        </div>
      </div>
      {action !== undefined ? <div>{action}</div> : null}
    </div>
  );
}

export interface RefusalBandProps {
  /** The refusal, in plain language. Never an enum, never an HTTP status. */
  readonly message: ReactNode;
  /** What to do about it. */
  readonly description?: ReactNode;
  /** A `SupportLine`. Admin only — never on a worker surface. */
  readonly support?: ReactNode;
  /** `inset` inside a dialog, `band` on a page. */
  readonly density?: 'band' | 'inset';
  readonly className?: string;
}

/**
 * A business refusal from the API (409 / 422) landing inside a form.
 *
 * The form KEEPS EVERY VALUE — this band appears above the fields, it does not
 * replace them. Losing a supervisor's typed reason because the server refused
 * the request is how a correct backend produces an incorrect timesheet.
 */
export function RefusalBand({
  message,
  description,
  support,
  density = 'inset',
  className,
}: RefusalBandProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Banner
        tone="danger"
        density={density}
        live="alert"
        title={message}
        {...(description !== undefined ? { description } : {})}
      />
      {support !== undefined ? <div className="px-1">{support}</div> : null}
    </div>
  );
}
